import { keccak256 } from 'js-sha3';

// Common function and error signatures. The selector is computed at load time
// from the canonical signature text, so entries can't drift out of sync.
const SIGNATURES = [
  // ERC-20 core + metadata + permit
  'totalSupply()',
  'balanceOf(address)',
  'transfer(address,uint256)',
  'transferFrom(address,address,uint256)',
  'approve(address,uint256)',
  'allowance(address,address)',
  'name()',
  'symbol()',
  'decimals()',
  'cap()',
  'increaseAllowance(address,uint256)',
  'decreaseAllowance(address,uint256)',
  'mint(address,uint256)',
  'burn(uint256)',
  'burnFrom(address,uint256)',
  'permit(address,address,uint256,uint256,uint8,bytes32,bytes32)',
  'permit(address,address,uint256,uint256,bool,uint8,bytes32,bytes32)',
  'nonces(address)',
  'DOMAIN_SEPARATOR()',

  // ERC-721
  'ownerOf(uint256)',
  'safeTransferFrom(address,address,uint256)',
  'safeTransferFrom(address,address,uint256,bytes)',
  'setApprovalForAll(address,bool)',
  'getApproved(uint256)',
  'isApprovedForAll(address,address)',
  'tokenURI(uint256)',
  'supportsInterface(bytes4)',
  'tokenByIndex(uint256)',
  'tokenOfOwnerByIndex(address,uint256)',
  'baseURI()',
  'royaltyInfo(uint256,uint256)',

  // ERC-1155
  'balanceOf(address,uint256)',
  'balanceOfBatch(address[],uint256[])',
  'safeTransferFrom(address,address,uint256,uint256,bytes)',
  'safeBatchTransferFrom(address,address,uint256[],uint256[],bytes)',
  'uri(uint256)',
  'setURI(string)',

  // ERC-4626 tokenized vaults
  'asset()',
  'totalAssets()',
  'convertToShares(uint256)',
  'convertToAssets(uint256)',
  'maxDeposit(address)',
  'deposit(uint256,address)',
  'maxMint(address)',
  'mint(uint256,address)',
  'maxWithdraw(address)',
  'withdraw(uint256,address,address)',
  'maxRedeem(address)',
  'redeem(uint256,address,address)',
  'previewDeposit(uint256)',
  'previewMint(uint256)',
  'previewWithdraw(uint256)',
  'previewRedeem(uint256)',

  // Ownership / access control / pausable
  'owner()',
  'transferOwnership(address)',
  'renounceOwnership()',
  'acceptOwnership()',
  'pendingOwner()',
  'hasRole(bytes32,address)',
  'getRoleAdmin(bytes32)',
  'grantRole(bytes32,address)',
  'revokeRole(bytes32,address)',
  'renounceRole(bytes32,address)',
  'paused()',
  'pause()',
  'unpause()',

  // Proxy patterns (EIP-1967, beacon, UUPS)
  'implementation()',
  'admin()',
  'changeAdmin(address)',
  'upgradeTo(address)',
  'upgradeToAndCall(address,bytes)',
  'proxiableUUID()',
  'upgrade(address)',
  'beacon()',
  'initialize()',

  // WETH
  'deposit()',
  'withdraw(uint256)',
  'withdraw()',

  // Multicall (Uniswap Multicall / Multicall3)
  'multicall(bytes[])',
  'multicall(uint256,bytes[])',
  'aggregate((address,bytes)[])',
  'tryAggregate(bool,(address,bytes)[])',
  'tryBlockAndAggregate(bool,(address,bytes)[])',
  'blockAndAggregate((address,bytes)[])',
  'aggregate3((address,bool,bytes)[])',
  'aggregate3Value((address,bool,uint256,bytes)[])',

  // Uniswap V2 router
  'factory()',
  'WETH()',
  'addLiquidity(address,address,uint256,uint256,uint256,uint256,address,uint256)',
  'addLiquidityETH(address,uint256,uint256,uint256,address,uint256)',
  'removeLiquidity(address,address,uint256,uint256,uint256,address,uint256)',
  'removeLiquidityETH(address,uint256,uint256,uint256,address,uint256)',
  'removeLiquidityWithPermit(address,address,uint256,uint256,uint256,address,uint256,bool,uint8,bytes32,bytes32)',
  'removeLiquidityETHWithPermit(address,uint256,uint256,uint256,address,uint256,bool,uint8,bytes32,bytes32)',
  'swapExactTokensForTokens(uint256,uint256,address[],address,uint256)',
  'swapTokensForExactTokens(uint256,uint256,address[],address,uint256)',
  'swapExactETHForTokens(uint256,address[],address,uint256)',
  'swapTokensForExactETH(uint256,uint256,address[],address,uint256)',
  'swapExactTokensForETH(uint256,uint256,address[],address,uint256)',
  'swapETHForExactTokens(uint256,address[],address,uint256)',
  'swapExactTokensForTokensSupportingFeeOnTransferTokens(uint256,uint256,address[],address,uint256)',
  'swapExactETHForTokensSupportingFeeOnTransferTokens(uint256,address[],address,uint256)',
  'swapExactTokensForETHSupportingFeeOnTransferTokens(uint256,uint256,address[],address,uint256)',
  'getAmountsOut(uint256,address[])',
  'getAmountsIn(uint256,address[])',
  'getAmountOut(uint256,uint256,uint256)',
  'getAmountIn(uint256,uint256,uint256)',
  'quote(uint256,uint256,uint256)',

  // Uniswap V2 pair + factory
  'token0()',
  'token1()',
  'getReserves()',
  'price0CumulativeLast()',
  'price1CumulativeLast()',
  'kLast()',
  'mint(address)',
  'burn(address)',
  'swap(uint256,uint256,address,bytes)',
  'skim(address)',
  'sync()',
  'initialize(address,address)',
  'feeTo()',
  'feeToSetter()',
  'getPair(address,address)',
  'allPairs(uint256)',
  'allPairsLength()',
  'createPair(address,address)',
  'setFeeTo(address)',
  'setFeeToSetter(address)',

  // Uniswap V3 SwapRouter + pool
  'exactInputSingle((address,address,uint24,address,uint256,uint256,uint256,uint160))',
  'exactInput((bytes,address,uint256,uint256,uint256))',
  'exactOutputSingle((address,address,uint24,address,uint256,uint256,uint256,uint160))',
  'exactOutput((bytes,address,uint256,uint256,uint256))',
  'uniswapV3SwapCallback(int256,int256,bytes)',
  'swap(address,bool,int256,uint160,bytes)',
  'flash(address,uint256,uint256,bytes)',
  'observe(uint32[])',
  'observations(uint256)',
  'slot0()',
  'fee()',
  'tickSpacing()',
  'liquidity()',
  'positions(bytes32)',

  // Chainlink price feeds + VRF
  'latestRoundData()',
  'latestAnswer()',
  'getRoundData(uint80)',
  'description()',
  'version()',
  'requestRandomness(bytes32,uint256)',
  'requestRandomWords(bytes32,uint16,uint32,uint32)',

  // ENS registry + resolver
  'owner(bytes32)',
  'resolver(bytes32)',
  'ttl(bytes32)',
  'addr(bytes32)',
  'setAddr(bytes32,address)',
  'name(bytes32)',
  'setName(bytes32,string)',
  'text(bytes32,string)',
  'contenthash(bytes32)',
  'setSubnodeOwner(bytes32,bytes32,address)',
  'setOwner(bytes32,address)',
  'setResolver(bytes32,address)',
  'setTTL(bytes32,uint64)',

  // Gnosis Safe
  'execTransaction(address,uint256,bytes,uint8,uint256,uint256,uint256,address,address,bytes)',
  'execTransactionFromModule(address,uint256,bytes,uint8)',
  'getOwners()',
  'getThreshold()',
  'isOwner(address)',
  'addOwnerWithThreshold(address,uint256)',
  'removeOwner(address,address,uint256)',
  'changeThreshold(uint256)',
  'nonce()',
  'setup(address[],uint256,address,bytes,address,address,uint256,address)',
  'getTransactionHash(address,uint256,bytes,uint8,uint256,uint256,uint256,address,address,uint256)',
  'enableModule(address)',
  'disableModule(address,address)',
  'isModuleEnabled(address)',

  // Governance (OpenZeppelin Governor + Timelock)
  'propose(address[],uint256[],bytes[],string)',
  'castVote(uint256,uint8)',
  'castVoteWithReason(uint256,uint8,string)',
  'proposalVotes(uint256)',
  'state(uint256)',
  'queue(address[],uint256[],bytes[],bytes32)',
  'execute(address[],uint256[],bytes[],bytes32)',
  'votingDelay()',
  'votingPeriod()',
  'quorum(uint256)',
  'schedule(address,uint256,bytes,bytes32,bytes32,uint256)',
  'execute(address,uint256,bytes,bytes32,bytes32)',
  'getMinDelay()',

  // Staking / airdrop / misc
  'stake(uint256)',
  'unstake(uint256)',
  'getReward()',
  'earned(address)',
  'stakingToken()',
  'rewardsToken()',
  'claim()',
  'claim(uint256,address,uint256,bytes32[])',
  'isClaimed(uint256)',
  'getBalance(address)',
  'testStr(string)',

  // Standard + OpenZeppelin custom errors (4-byte selectors, same hashing)
  'Error(string)',
  'Panic(uint256)',
  'FailedCall()',
  'FailedInnerCall()',
  'AddressInsufficientBalance(address)',
  'AddressEmptyCode(address)',
  'OwnableUnauthorizedAccount(address)',
  'OwnableInvalidOwner(address)',
  'AccessControlUnauthorizedAccount(address,bytes32)',
  'AccessControlBadConfirmation()',
  'ERC20InsufficientBalance(address,uint256,uint256)',
  'ERC20InvalidSender(address)',
  'ERC20InvalidReceiver(address)',
  'ERC20InsufficientAllowance(address,uint256,uint256)',
  'ERC20InvalidApprover(address)',
  'ERC20InvalidSpender(address)',
  'ERC721NonexistentToken(uint256)',
  'ERC721IncorrectOwner(address,uint256,address)',
  'ERC721InvalidSender(address)',
  'ERC721InvalidReceiver(address)',
  'ERC721InvalidOwner(address)',
  'ERC721InvalidOperator(address)',
  'ERC721InvalidApprover(address)',
  'ERC721InsufficientApproval(address,uint256)',
  'ERC1155InsufficientBalance(address,uint256,uint256,uint256)',
  'ERC1155InvalidSender(address)',
  'ERC1155InvalidReceiver(address)',
  'ERC1155MissingApprovalForAll(address,address)',
  'ERC1155InvalidApprover(address)',
  'ERC1155InvalidOperator(address)',
  'ERC1155InvalidArrayLength(uint256,uint256)',
  'SafeERC20FailedOperation(address)',
  'SafeERC20FailedDecreaseAllowance(address,uint256,uint256)',
  'ReentrancyGuardReentrantCall()',
  'EnforcedPause()',
  'ExpectedPause()',
  'InvalidInitialization()',
  'NotInitializing()',
  'ECDSAInvalidSignature()',
  'ECDSAInvalidSignatureLength(uint256)',
  'ECDSAInvalidSignatureS(bytes32)',
];

// selector -> [signatures] — multiple entries share a selector on collisions
export const SELECTOR_DB = SIGNATURES.reduce((db, sig) => {
  const selector = '0x' + keccak256(sig).slice(0, 8);
  const list = db.get(selector);
  if (list) list.push(sig);
  else db.set(selector, [sig]);
  return db;
}, new Map());

export function lookupSelector(input) {
  const hex = input.trim().toLowerCase().replace(/^0x/, '');
  if (!/^[0-9a-f]+$/.test(hex)) return { error: 'Enter hexadecimal — a 4-byte selector like 0xa9059cbb, or paste full calldata.' };
  if (hex.length < 8) return { error: 'A selector is 4 bytes (8 hex characters).' };
  const selector = '0x' + hex.slice(0, 8);
  const matches = SELECTOR_DB.get(selector);
  if (matches) return { selector, matches };
  return { selector, matches: [] };
}
